import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, ChevronRight, Images, Lock, Radio, Smile } from 'lucide-react';
import { api } from '../api/client.ts';
import { isNative } from '../api/origin.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Avatar } from '../components/Avatar.tsx';
import { NotificationSettingsDialog } from '../components/NotificationSettingsDialog.tsx';
import { buildAt, versionLine, type ShellVersion } from '../lib/appVersion.ts';
import { formatPasswordAge } from '../lib/format.ts';
import { checkRelay, describeProbe, describeRelay, relayCheckSummary } from '../lib/relayCheck.ts';
import { reportClientError } from '../lib/errorReporter.ts';
import { setSmileyBarShown, useSmileyBar } from '../lib/smileyBar.ts';
import { themeBackground, themeName, useTheme } from '../lib/theme.ts';
import './SettingsScreen.css';

/**
 * The app's preferences, reached from the chat list's ⋮ menu. Notifications
 * stay a dialog — they are a short form, not a screen — but they are opened
 * from here rather than from an icon in the list's header, so there is one
 * place to look for a setting.
 */
export function SettingsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const smileyBar = useSmileyBar();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [shell, setShell] = useState<ShellVersion | null>(null);
  const [relayLines, setRelayLines] = useState<string[] | null>(null);
  const [relayBusy, setRelayBusy] = useState(false);

  /**
   * The relay, as seen from the device this screen is open on. The server only
   * knows that a relay is configured; whether it answers is a property of the
   * network the device is on, so the check lives here, on everyone's phone,
   * rather than on an admin page — the network worth asking about is mobile
   * data, and the admin is rarely the one standing on it.
   *
   * The result is also filed as a client error report on purpose: the person
   * pressing the button on the broken network and the person who can fix the
   * relay are rarely the same person, and the screen's lines die with the
   * screen. `client_errors` is the one channel the device already has for
   * «здесь было вот так», and this is exactly that kind of fact.
   */
  async function runRelayCheck() {
    setRelayBusy(true);
    try {
      const report = await checkRelay(await api.iceConfig());
      setRelayLines([describeRelay(report), ...report.probes.map(describeProbe)]);
      reportClientError({
        kind: 'call',
        detail: 'relay-check',
        message: relayCheckSummary(report),
      });
    } catch {
      setRelayLines(['Не удалось спросить сервер про релей']);
    }
    setRelayBusy(false);
  }

  // The shell's own version, asked once per visit. Dynamic import for the same
  // reason useNativeBack.ts gives: the web bundle must never fetch the plugin,
  // and the bundler cannot see `isNative` through the module boundary.
  useEffect(() => {
    if (!isNative) return;
    let alive = true;
    void import('@capacitor/app')
      .then(async ({ App }) => {
        const info = await App.getInfo();
        if (alive) setShell({ version: info.version, build: info.build });
      })
      // A shell too old to answer — the line falls back to the build date,
      // which is still the useful half.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="app-frame settings-screen">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => navigate('/')}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="settings-heading">Настройки</h2>
      </header>

      <div className="settings-list">
        {/* First, and the only row whose tile is a photograph: it is showing
            what everyone else sees of you, the way the theme row below shows
            the wallpaper rather than describing it. */}
        <button
          type="button"
          className="settings-item"
          onClick={() => navigate('/settings/profile')}
        >
          <Avatar
            className="settings-item-swatch"
            photoId={user?.avatarPhotoId}
            name={user?.displayName}
          />
          <div className="settings-item-body">
            <div className="settings-item-label">{user?.displayName}</div>
            <div className="settings-item-note">@{user?.username} · имя и фото</div>
          </div>
          <ChevronRight className="settings-item-chevron" size={17} />
        </button>

        <button
          type="button"
          className="settings-item"
          onClick={() => navigate('/settings/theme')}
        >
          {/* The swatch is the theme's own wallpaper — a picked photo included,
              so the row says which one is on without opening the screen. */}
          <div className="settings-item-swatch" style={{ background: themeBackground(theme) }} />
          <div className="settings-item-body">
            <div className="settings-item-label">Оформление</div>
            <div className="settings-item-note">
              {themeName(theme)} · применяется ко всему приложению
            </div>
          </div>
          <ChevronRight className="settings-item-chevron" size={17} />
        </button>

        {/* Between the two rows about how the app looks and the one about what
            it may interrupt you with: it is the only row here that is about the
            account rather than the device. */}
        <button
          type="button"
          className="settings-item"
          onClick={() => navigate('/settings/password')}
        >
          <div className="settings-item-icon">
            <Lock size={18} />
          </div>
          <div className="settings-item-body">
            <div className="settings-item-label">Пароль</div>
            <div className="settings-item-note">
              {formatPasswordAge(user?.passwordChangedAt ?? null)}
            </div>
          </div>
          <ChevronRight className="settings-item-chevron" size={17} />
        </button>

        <button
          type="button"
          className="settings-item"
          onClick={() => setNotificationsOpen(true)}
        >
          <div className="settings-item-icon">
            <Bell size={18} />
          </div>
          <div className="settings-item-body">
            <div className="settings-item-label">Уведомления</div>
            <div className="settings-item-note">Push на этом устройстве</div>
          </div>
          <ChevronRight className="settings-item-chevron" size={17} />
        </button>

        {/* A label rather than a button: the whole row toggles, and the
            checkbox is what says which way it is set. */}
        <label className="settings-item settings-item-toggle">
          <div className="settings-item-icon">
            <Smile size={18} />
          </div>
          <div className="settings-item-body">
            <div className="settings-item-label">Панель смайлов</div>
            <div className="settings-item-note">Ряд частых смайлов над полем ввода</div>
          </div>
          <input
            type="checkbox"
            className="settings-item-check"
            checked={smileyBar}
            onChange={(event) => setSmileyBarShown(event.target.checked)}
          />
        </label>

        {/* Only inside the app: a page cannot enumerate a phone's pictures, so
            on the web this row could only ever lead somewhere that says so.
            A row rather than a toggle beside the one above it, because the
            explanation has to be read before iOS asks its one question — see
            GalleryScreen.tsx. */}
        {isNative && (
          <button
            type="button"
            className="settings-item"
            onClick={() => navigate('/settings/gallery')}
          >
            <div className="settings-item-icon">
              <Images size={18} />
            </div>
            <div className="settings-item-body">
              <div className="settings-item-label">Расширенный доступ (тест режим)</div>
              <div className="settings-item-note"></div>
            </div>
            <ChevronRight className="settings-item-chevron" size={17} />
          </button>
        )}

        {/* Runs in place instead of navigating: the answer is a handful of
            lines, and a screen around them would only restate the note. No
            chevron for the same reason. */}
        <button
          type="button"
          className="settings-item"
          disabled={relayBusy}
          onClick={() => void runRelayCheck()}
        >
          <div className="settings-item-icon">
            <Radio size={18} />
          </div>
          <div className="settings-item-body">
            <div className="settings-item-label">Проверить релей</div>
            <div className="settings-item-note">
              {relayBusy ? (
                'Проверяю…'
              ) : relayLines === null ? (
                'Если звонки не соединяются — нажать по мобильному интернету, без Wi-Fi'
              ) : (
                relayLines.map((line, index) => <div key={index}>{line}</div>)
              )}
            </div>
          </div>
        </button>

        {/* Which build this is, on every platform — the first thing to read
            off both phones when one of them behaves differently. Inside the
            list so it scrolls with it, after the rows so it reads as a
            colophon rather than a setting. */}
        <div className="settings-version">{versionLine(shell, buildAt)}</div>
      </div>

      {notificationsOpen && (
        <NotificationSettingsDialog onClose={() => setNotificationsOpen(false)} />
      )}
    </div>
  );
}
